import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { format, parseISO } from 'date-fns';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import '../App2.css';
import { useForecast } from "../hooks/useForecast.ts";

// Weather code to description mapping
const WEATHER_CODES = [
  { id: 0, description: 'Clear sky' },
  { id: 1, description: 'Mainly clear' },
  { id: 2, description: 'Partly cloudy' },
  { id: 3, description: 'Overcast' },
  { id: 45, description: 'Fog and depositing rime fog' },
  { id: 48, description: 'Fog and depositing rime fog' },
  { id: 51, description: 'Drizzle: Light intensity' },
  { id: 53, description: 'Drizzle: Moderate intensity' },
  { id: 55, description: 'Drizzle: Dense intensity' },
  { id: 56, description: 'Freezing Drizzle: Light intensity' },
  { id: 57, description: 'Freezing Drizzle: Dense intensity' },
  { id: 61, description: 'Rain: Slight intensity' },
  { id: 63, description: 'Rain: Moderate intensity' },
  { id: 65, description: 'Rain: Heavy intensity' },
  { id: 66, description: 'Freezing Rain: Light intensity' },
  { id: 67, description: 'Freezing Rain: Heavy intensity' },
  { id: 71, description: 'Snow fall: Slight' },
  { id: 73, description: 'Snow fall: Moderate' },
  { id: 75, description: 'Snow fall: Heavy intensity' },
  { id: 77, description: 'Snow grains' },
  { id: 80, description: 'Rain showers: Slight' },
  { id: 81, description: 'Rain showers: Moderate' },
  { id: 82, description: 'Rain showers: Violent' },
  { id: 85, description: 'Snow showers: Slight' },
  { id: 86, description: 'Snow showers: Heavy' },
  { id: 95, description: 'Thunderstorm: Slight or moderate' },
  { id: 96, description: 'Thunderstorm with slight and heavy hail' },
  { id: 99, description: 'Thunderstorm with slight and heavy hail' }
];

// Helper to map weather code to icon
function getWeatherIcon(code) {
  if ([0, 1].includes(code)) return <span style={{ fontSize: 56 }}>☀️</span>;
  if ([2, 3].includes(code)) return <span style={{ fontSize: 56 }}>⛅️</span>;
  if ([51, 53, 55, 61, 63, 65, 80, 81, 82].includes(code)) return <span style={{ fontSize: 56 }}>🌧️</span>;
  if ([71, 73, 75, 77, 85, 86].includes(code)) return <span style={{ fontSize: 56 }}>❄️</span>;
  if ([45, 48].includes(code)) return <span style={{ fontSize: 56 }}>🌫️</span>;
  if ([95, 96, 99].includes(code)) return <span style={{ fontSize: 56 }}>⛈️</span>;
  return <span style={{ fontSize: 56 }}>🌡️</span>;
}

function Forecast() {
  const { lat, long } = useParams();
  const { data, setParams, isLoading, error } = useForecast();

  useEffect(() => {
    if (lat && long) {
      setParams({
        latitude: lat,
        longitude: long,
        current_weather: true
      });
    }
  }, [lat, long, setParams]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Error loading forecast data: {error.message}
      </Alert>
    );
  }

  if (!data || !data.current_weather) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        No weather data available for this location.
      </Alert>
    );
  }

  return (
    <main>
      <Members weather={data} />
    </main>
  );
}

export function Members({ weather }) {
  const currentWeather = weather.current_weather;
  const weatherInfo = WEATHER_CODES.find(item =>
    item.id === currentWeather.weathercode
  ) || { description: 'Unknown weather condition' };
  const formattedTime = currentWeather?.time ?
    format(parseISO(currentWeather.time), 'HH:mm') :
    'Unknown time';

  // New fields
  const elevation = weather.elevation;
  const timezone = weather.timezone_abbreviation || weather.timezone;
  const interval = currentWeather.interval;

    // Convert Celsius to Fahrenheit
  const tempC = currentWeather.temperature;
  const tempF = (tempC * 9/5 + 32).toFixed(1);

  return (
      <div className="two-column-page-content" style={{ background: "#f5f7fa", minHeight: "100vh" }}>
      <div className="page-column-1">
        <div className="page-content content-module" style={{
          maxWidth: 420,
          margin: "2rem auto",
          borderRadius: 16,
          background: "#fff",
          boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
          padding: "1.2rem"
        }}>
          <div style={{ textAlign: "center", marginBottom: 16 }}>
            {getWeatherIcon(currentWeather.weathercode)}
            <div style={{ fontSize: "2.2rem", fontWeight: 700, margin: "0.5rem 0" }}>
              {tempC}°C / {tempF}°F
            </div>
            <div style={{ color: "#789", fontSize: "1rem" }}>
              {weatherInfo.description}
            </div>
            <div style={{ color: "#aaa", fontSize: "0.95rem", marginTop: 4 }}>
              {formattedTime}
            </div>
          </div>
          <div className="cur-con-weather-card__panel details-container" style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 6,
            flexWrap: "wrap"
          }}>
            <div className="spaced-content detail" style={{
              flex: "1 1 40%",
              background: "#f0f4f8",
              borderRadius: 8,
              padding: "0.4rem 0.2rem",
              textAlign: "center",
              minWidth: 80,
              maxWidth: 120,
              marginBottom: 6
            }}>
              <span className="label" style={{ display: "block", color: "#789", fontSize: "0.8rem" }}>Wind</span>
              <span className="value" style={{ fontWeight: 600, fontSize: "0.95rem" }}>{currentWeather.windspeed} km/h</span>
            </div>
            <div className="spaced-content detail" style={{
              flex: "1 1 40%",
              background: "#f0f4f8",
              borderRadius: 8,
              padding: "0.4rem 0.2rem",
              textAlign: "center",
              minWidth: 80,
              maxWidth: 120,
              marginBottom: 6
            }}>
              <span className="label" style={{ display: "block", color: "#789", fontSize: "0.8rem" }}>Direction</span>
              <span className="value" style={{ fontWeight: 600, fontSize: "0.95rem" }}>{currentWeather.winddirection}°</span>
            </div>
            <div className="spaced-content detail" style={{
              flex: "1 1 40%",
              background: "#f0f4f8",
              borderRadius: 8,
              padding: "0.4rem 0.2rem",
              textAlign: "center",
              minWidth: 80,
              maxWidth: 120,
              marginBottom: 6
            }}>
              <span className="label" style={{ display: "block", color: "#789", fontSize: "0.8rem" }}>Elevation</span>
              <span className="value" style={{ fontWeight: 600, fontSize: "0.95rem" }}>{elevation} m</span>
            </div>
            <div className="spaced-content detail" style={{
              flex: "1 1 40%",
              background: "#f0f4f8",
              borderRadius: 8,
              padding: "0.4rem 0.2rem",
              textAlign: "center",
              minWidth: 80,
              maxWidth: 120,
              marginBottom: 6
            }}>
              <span className="label" style={{ display: "block", color: "#789", fontSize: "0.8rem" }}>Timezone</span>
              <span className="value" style={{ fontWeight: 600, fontSize: "0.95rem" }}>{timezone}</span>
            </div>
            <div className="spaced-content detail" style={{
              flex: "1 1 40%",
              background: "#f0f4f8",
              borderRadius: 8,
              padding: "0.4rem 0.2rem",
              textAlign: "center",
              minWidth: 80,
              maxWidth: 120,
              marginBottom: 6
            }}>
              <span className="label" style={{ display: "block", color: "#789", fontSize: "0.8rem" }}>Interval</span>
              <span className="value" style={{ fontWeight: 600, fontSize: "0.95rem" }}>{interval} s</span>
            </div>
          </div>
          {/* Responsive styles */}
          <style>
            {`
              @media (max-width: 600px) {
                .details-container {
                  flex-direction: column !important;
                  gap: 4px !important;
                }
                .spaced-content.detail {
                  min-width: 0 !important;
                  max-width: 100% !important;
                  margin-bottom: 6px !important;
                }
                .page-content.content-module {
                  padding: 0.7rem !important;
                }
              }
            `}
          </style>
        </div>
      </div>
    </div>
  );
}

export default Forecast;